const STAGGER_DURATION = 3;
const STAR_ANIMATION_DURATION = 2.5;
const INITIAL_PHASE_END = STAGGER_DURATION + STAR_ANIMATION_DURATION;
const FULL_CYCLE = INITIAL_PHASE_END * 2;

const STAR_PATH_DATA =
    'M0.36655 4.98197C2.85366 5.66931 3.33173 6.1466 4.01813 8.63337C4.07813 8.84998 4.275 9 4.5 9C4.725 9 4.92187 8.84997 4.98187 8.63337C5.66812 6.14573 6.14625 5.66828 8.63345 4.98197C8.85001 4.92196 9 4.72505 9 4.5C9 4.27495 8.84906 4.07804 8.63345 4.01803C6.14634 3.33163 5.66827 2.8534 4.98187 0.366629C4.92187 0.150022 4.725 0 4.5 0C4.275 0 4.07813 0.150032 4.01813 0.366629C3.33188 2.85331 2.85375 3.33173 0.36655 4.01803C0.14999 4.07804 0 4.27495 0 4.5C0 4.72505 0.149999 4.92196 0.36655 4.98197Z';

interface Star {
    initialX: number;
    initialY: number;
    size: number;
    animationDelay: number;
    amplitudeX: number;
    amplitudeY: number;
    freqX: number;
    freqY: number;
    phaseX: number;
    phaseY: number;
}

export class AiLoaderElement extends HTMLElement {
    private static readonly CLIP_PATH_ID = 'pdp_ai-loader-o-clip';
    private static readonly STYLE_ID = 'pdp_ai-loader-styles';

    private static readonly STYLES = `
        .pdp_ai-loader {
            position: relative;
            display: flex;
            justify-content: center;
            align-items: center;
            min-height: 200px;
            overflow: hidden;
        }
        .pdp_ai-loader canvas {
            aspect-ratio: 9 / 10;
            max-height: 100%;
            max-width: 100%;
            clip-path: url(#pdp_ai-loader-o-clip);
        }
    `;

    public static get observedAttributes(): string[] {
        return ['density', 'opacity'];
    }

    private readonly starPath = new Path2D(STAR_PATH_DATA);

    private canvasElement: HTMLCanvasElement | null = null;
    private stars: Star[] = [];
    private canvasWidth = 0;
    private canvasHeight = 0;
    private pixelRatio = 1;
    private ctx: CanvasRenderingContext2D | null = null;
    private animationStartTime = 0;
    private animationFrameId: number | null = null;
    private resizeObserver: ResizeObserver | null = null;

    public connectedCallback() {
        if (!this.isConnected) return;
        AiLoaderElement.injectStyles();
        this.build();

        /*                                                                         */
        /*                                                                                    */
        this.resizeObserver = new ResizeObserver(entries => {
            const { width, height } = entries[0]?.contentRect ?? {};
            if (!width || !height) return;

            this.resizeObserver?.disconnect();
            this.resizeObserver = null;

            this.initializeCanvas();
            this.initializeStars();
            this.animationFrameId = requestAnimationFrame(t => this.tick(t));
        });

        this.resizeObserver.observe(this.canvasElement!);
    }

    public disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.resizeObserver = null;
        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }
    }

    public get density(): number {
        return parseInt(this.getAttribute('density') ?? '', 10) || 500;
    }

    public set density(value: number) {
        this.setAttribute('density', String(value));
    }

    public get opacity(): number {
        const val = parseFloat(this.getAttribute('opacity') ?? '');
        return isNaN(val) ? 1 : val;
    }

    public set opacity(value: number) {
        this.setAttribute('opacity', String(value));
    }

    private static injectStyles() {
        if (document.getElementById(AiLoaderElement.STYLE_ID)) return;
        const style = document.createElement('style');
        style.id = AiLoaderElement.STYLE_ID;
        style.textContent = AiLoaderElement.STYLES;
        document.head.appendChild(style);
    }

    private build() {
        this.classList.add('pdp_ai-loader');

        const svg = document.createElementNS('http:/*                           */
        svg.setAttribute('width', '0');
        svg.setAttribute('height', '0');
        svg.style.position = 'absolute';

        const defs = document.createElementNS('http:/*                            */
        const clipPath = document.createElementNS('http:/*                                */
        clipPath.setAttribute('id', AiLoaderElement.CLIP_PATH_ID);
        clipPath.setAttribute('clipPathUnits', 'objectBoundingBox');

        const path = document.createElementNS('http:/*                            */
        path.setAttribute('transform', 'scale(0.02777777778, 0.025)');
        path.setAttribute(
            'd',
            'M33.1123 6.23436C30.5412 2.07053 25.9223 0 20.0975 0C9.0167 0 1.80396 6.39363 0.52978 20.0455L0.370508 21.7747C-0.767148 33.9704 5.64923 40 15.5924 40C26.6731 40 33.8631 33.5381 35.16 19.8862L35.3193 18.157C35.5936 15.2449 35.3193 9.74599 33.1123 6.23436ZM24.2385 17.4289L24.1248 18.7258C23.3739 27.9636 20.3705 30.8305 16.6617 30.8305C13.317 30.8305 10.9735 28.5779 11.4513 22.5483L11.565 21.2059C12.2931 11.9909 15.3193 9.0785 19.0281 9.0785C22.3045 9.0785 24.7164 11.3994 24.2385 17.4289Z'
        );

        clipPath.appendChild(path);
        defs.appendChild(clipPath);
        svg.appendChild(defs);

        const canvas = document.createElement('canvas');

        this.appendChild(svg);
        this.appendChild(canvas);
        this.canvasElement = canvas;
    }

    private initializeCanvas() {
        if (!this.canvasElement) return;

        this.pixelRatio = window.devicePixelRatio || 1;

        const rect = this.canvasElement.getBoundingClientRect();
        this.canvasWidth = rect.width;
        this.canvasHeight = rect.height;
        this.canvasElement.width = this.canvasWidth * this.pixelRatio;
        this.canvasElement.height = this.canvasHeight * this.pixelRatio;

        this.ctx = this.canvasElement.getContext('2d');
        if (this.ctx) {
            this.ctx.scale(this.pixelRatio, this.pixelRatio);
        }
    }

    private initializeStars() {
        if (!this.canvasElement) return;

        const newStars: Star[] = [];
        const rect = this.canvasElement.getBoundingClientRect();
        const density = this.density;

        const gridSize = Math.ceil(Math.sqrt(density));
        const cellWidth = rect.width / gridSize;
        const cellHeight = rect.height / gridSize;

        for (let i = 0; i < density; i++) {
            const u1 = Math.random();
            const u2 = Math.random();
            const z0 = Math.sqrt(-2.0 * Math.log(u1)) * Math.cos(2.0 * Math.PI * u2);
            const size = Math.max(0.5, 2 + z0 * 2.5);

            const gridX = i % gridSize;
            const gridY = Math.floor(i / gridSize);

            const x = gridX * cellWidth + Math.random() * cellWidth;
            const y = gridY * cellHeight + Math.random() * cellHeight;

            const amplitude = 3 + Math.random() * 3;
            const baseFreq = 0.1 + Math.random() * 0.15;

            newStars.push({
                initialX: x,
                initialY: y,
                size,
                animationDelay: Math.random() * STAGGER_DURATION,
                amplitudeX: amplitude,
                amplitudeY: amplitude * (0.5 + Math.random() * 0.8),
                freqX: baseFreq,
                freqY: baseFreq * (0.6 + Math.random() * 0.8),
                phaseX: Math.random() * Math.PI * 2,
                phaseY: Math.random() * Math.PI * 2,
            });
        }
        this.stars = newStars;
    }

    private tick(currentTime: number) {
        if (!this.ctx || !this.canvasElement) return;

        if (this.animationStartTime === 0) {
            this.animationStartTime = currentTime;
        }

        const elapsedTime = (currentTime - this.animationStartTime) / 1000;

        this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);
        this.ctx.fillStyle = `rgba(82, 46, 183, ${this.opacity})`;

        for (const star of this.stars) {
            const x = star.initialX + star.amplitudeX * Math.sin(elapsedTime * star.freqX * Math.PI * 2 + star.phaseX);
            const y = star.initialY + star.amplitudeY * Math.sin(elapsedTime * star.freqY * Math.PI * 2 + star.phaseY);

            const scale = this.calculateStarScale(star, elapsedTime);
            if (scale !== null) {
                this.drawStar(this.ctx, x, y, star.size, scale);
            }
        }

        this.animationFrameId = requestAnimationFrame(t => this.tick(t));
    }

    private calculateStarScale(star: Star, elapsedTime: number): number | null {
        const timeSinceDelay = elapsedTime - star.animationDelay;

        if (elapsedTime < INITIAL_PHASE_END) {
            return this.calculateForwardScale(timeSinceDelay);
        }

        const pulseElapsedTime = elapsedTime - INITIAL_PHASE_END;
        return this.calculatePulsingScale(pulseElapsedTime, star.animationDelay);
    }

    private calculateForwardScale(timeSinceDelay: number): number | null {
        if (timeSinceDelay < 0) return null;
        return Math.min(timeSinceDelay / STAR_ANIMATION_DURATION, 1);
    }

    private calculatePulsingScale(pulseElapsedTime: number, starAnimationDelay: number): number {
        const cycleTime = pulseElapsedTime % FULL_CYCLE;

        if (cycleTime < INITIAL_PHASE_END) {
            const reverseDelay = STAGGER_DURATION - starAnimationDelay;
            const timeSinceReverseDelay = cycleTime - reverseDelay;

            if (timeSinceReverseDelay < 0) return 1;
            if (timeSinceReverseDelay < STAR_ANIMATION_DURATION) {
                return 1 - (timeSinceReverseDelay / STAR_ANIMATION_DURATION) * 0.3;
            }
            return 0.7;
        } else {
            const forwardTime = cycleTime - INITIAL_PHASE_END;
            const timeSinceDelay = forwardTime - starAnimationDelay;

            if (timeSinceDelay < 0) return 0.7;
            if (timeSinceDelay < STAR_ANIMATION_DURATION) {
                return 0.7 + (timeSinceDelay / STAR_ANIMATION_DURATION) * 0.3;
            }
            return 1;
        }
    }

    private drawStar(context: CanvasRenderingContext2D, x: number, y: number, size: number, scale: number) {
        context.save();
        context.translate(x, y);
        context.scale(size, size);
        /*                                 */
        context.translate(4.5, 4.5);
        context.scale(scale, scale);
        context.translate(-4.5, -4.5);
        context.fill(this.starPath);
        context.restore();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'ai-loader': AiLoaderElement;
    }
}
